/**
 * "Needs review" — the ONE place for everything on this board that is waiting for a human (20 Oct 2026, Dean + Sales).
 * Autopilot suggestions that won't fire on their own, Autopilot flags ("manual processing needed"), parked / failed
 * trigger events and Cody proposals, grouped per deal. The backend reconciles against the live board before answering,
 * so anything the team has already sorted on Monday is not here. The Autopilot and Cody buttons keep their settings /
 * logs but no longer carry their own attention badges — this count is the only one.
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, AlertTriangle, Search, X, ExternalLink, Check, Bot, Zap, PauseCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  mirrorNeedsReview, mirrorAutopilotApprove, mirrorAutopilotDismiss, mirrorAutopilotDismissFlag,
  mirrorEventsApprove, mirrorEventsDismiss, codyApprove, codyDismiss,
} from "@/lib/api";

const SOURCE = {
  autopilot: { label: "Autopilot", cls: "bg-violet-50 text-violet-700 border-violet-300", Icon: Zap },
  event: { label: "Trigger", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: PauseCircle },
  cody: { label: "Cody", cls: "bg-sky-50 text-sky-700 border-sky-300", Icon: Bot },
};

export function MirrorNeedsReviewButton({ count, onOpenItem, onOpenCody, onChanged }) {
  const [open, setOpen] = useState(false);
  const quiet = !count;
  return (
    <>
      <Button size="sm" variant="outline"
        className={`h-8 text-xs ${quiet ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                        : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
        onClick={() => setOpen(true)} data-testid="mirror-needs-review-btn">
        {quiet ? <Check className="w-3.5 h-3.5 mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
        Needs review
        <span className={`ml-1.5 text-white rounded-full px-1.5 text-[10px] ${quiet ? "bg-emerald-600" : "bg-amber-600"}`}
          data-testid="mirror-needs-review-badge">{count || 0}</span>
      </Button>
      {open && (
        <NeedsReviewDialog onClose={() => setOpen(false)} onOpenItem={onOpenItem} onOpenCody={onOpenCody} onChanged={onChanged} />
      )}
    </>
  );
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 16) : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function NeedsReviewDialog({ onClose, onOpenItem, onOpenCody, onChanged }) {
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const actor = user?.name || "Ops";

  const load = useCallback(async () => {
    try { setData(await mirrorNeedsReview()); }
    catch { toast.error("Failed to load the needs-review list"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); if ([404, 409].includes(e?.response?.status)) { await load(); onChanged?.(); } }
    finally { setBusy(null); }
  };
  const approve = (it) => {
    if (it.source === "autopilot") return run(it.id, () => mirrorAutopilotApprove(it.id, actor), "Done");
    if (it.source === "event") return run(it.id, () => mirrorEventsApprove(it.id, actor), "Sent");
    return run(it.id, () => codyApprove(it.id), "Applied");
  };
  const dismiss = (it) => {
    if (it.source === "autopilot" && it.actions.includes("clear")) return run(it.id, () => mirrorAutopilotDismissFlag(it.item_id, it.stage), "Cleared");
    if (it.source === "autopilot") return run(it.id, () => mirrorAutopilotDismiss(it.id, actor), "Dismissed");
    if (it.source === "event") return run(it.id, () => mirrorEventsDismiss(it.id, actor), "Dismissed");
    return run(it.id, () => codyDismiss(it.id), "Dismissed");
  };

  const needle = q.trim().toLowerCase();
  const deals = (data?.deals || [])
    .map(d => ({ ...d, items: d.items.filter(i => source === "all" || i.source === source) }))
    .filter(d => d.items.length && (!needle || `${d.item_name} ${d.sf_number}`.toLowerCase().includes(needle)));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="mirror-needs-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Needs review
            {data && (
              <Badge variant="outline" className="text-[10px]" data-testid="mirror-needs-review-total">
                {data.total_deals} deal{data.total_deals === 1 ? "" : "s"} · {data.total_items} item{data.total_items === 1 ? "" : "s"}
              </Badge>
            )}
            <span className="text-[11px] font-normal text-slate-500">only what still needs a human — sorted on Monday = gone from here</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SF number or name…"
              className="h-8 text-xs pl-7" data-testid="mirror-needs-review-search" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {["all", "autopilot", "event", "cody"].map(s => (
              <button key={s} type="button" onClick={() => setSource(s)}
                className={`px-2 py-0.5 rounded text-[11px] border ${source === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600"}`}
                data-testid={`mirror-needs-review-source-${s}`}>
                {s === "all" ? "All" : SOURCE[s].label}{data?.by_source?.[s] != null ? ` (${data.by_source[s]})` : ""}
              </button>
            ))}
          </div>
        </div>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" /> : (
          <div className="space-y-2" data-testid="mirror-needs-review-list">
            {deals.length === 0 && (
              <div className="text-xs text-center py-8 text-emerald-700" data-testid="mirror-needs-review-empty">
                <Check className="w-5 h-5 mx-auto mb-1" /> Nothing needs a human right now{q || source !== "all" ? " for that filter" : ""}.
              </div>
            )}
            {deals.map(d => (
              <div key={d.item_id} className="border rounded-lg p-2.5 bg-white" data-testid={`mirror-needs-review-deal-${d.item_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-xs font-semibold truncate">{d.item_name || d.item_id}</span>
                    {d.status && <Badge variant="outline" className="text-[10px]">{d.status}</Badge>}
                    {!d.on_board && <Badge variant="outline" className="text-[10px] text-slate-400">not on the live board</Badge>}
                  </div>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] shrink-0"
                    onClick={() => { onOpenItem?.(d.item_id); onClose(); }} data-testid={`mirror-needs-review-open-${d.item_id}`}>
                    <ExternalLink className="w-3 h-3 mr-1" /> Open
                  </Button>
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {d.items.map(it => {
                    const S = SOURCE[it.source];
                    const k = `${it.source}:${it.id}`;
                    return (
                      <div key={k} className="flex flex-wrap items-start justify-between gap-2 rounded-md bg-slate-50 px-2 py-1.5" data-testid={`mirror-needs-review-row-${k}`}>
                        <div className="min-w-0 flex-1 basis-[220px]">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-[9px] gap-0.5 ${S.cls}`}><S.Icon className="w-2.5 h-2.5" />{S.label}</Badge>
                            <span className="text-[11px] font-medium text-slate-800">{it.title}</span>
                            {it.confidence != null && <span className="text-[10px] text-slate-400">{it.confidence}%</span>}
                            <span className="text-[10px] text-slate-400">{fmt(it.at)}</span>
                          </div>
                          {it.why && <div className="text-[11px] text-slate-600 mt-0.5" data-testid={`mirror-needs-review-why-${k}`}>{it.why}</div>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-auto">
                          {it.actions.includes("approve") && (
                            <Button size="sm" className="h-6 px-2 text-[10px]" disabled={busy === it.id} onClick={() => approve(it)}
                              data-testid={`mirror-needs-review-approve-${k}`}>
                              {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />{it.source === "event" ? "Approve — send" : "Approve"}</>}
                            </Button>
                          )}
                          {it.actions.includes("open_cody") && (
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => { onOpenCody?.(); onClose(); }}
                              data-testid={`mirror-needs-review-cody-${k}`}>
                              <Bot className="w-3 h-3 mr-1" /> Decide in Cody
                            </Button>
                          )}
                          {(it.actions.includes("dismiss") || it.actions.includes("clear")) && (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-slate-500" disabled={busy === it.id} onClick={() => dismiss(it)}
                              data-testid={`mirror-needs-review-dismiss-${k}`}>
                              <X className="w-3 h-3 mr-1" /> {it.actions.includes("clear") ? "Clear" : "Dismiss"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
